import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
// @ts-expect-error: Incorrect type
import InfiniteScroller from '@zestia/ember-simple-infinite-scroller/components/infinite-scroller';
import { t } from 'ember-intl';
import Fuse from 'fuse.js';
import LoadingBar from 'lumen/components/general/loading-bar';
import ParityInsightBreakdownBlockTitle from 'lumen/components/parity/insight/breakdown-block-title';
import ParityInsightBreakdownValue from 'lumen/components/parity/insight/breakdown-value';
import ParityInsightBreakdownValueLoading from 'lumen/components/parity/insight/breakdown-value-loading';
import type ParityInsightSegmentationItem from 'lumen/models/parity/insight/segmentation-item';
import {
  type Breakdown,
  PA_SORT_TYPES,
  type ParityFilter,
} from 'lumen/utils/constants/pi-constant-values';
import BreakdownItemOther, {
  isOtherRow,
} from 'lumen/utils/parity/datamodels/breakdown-item-other';
import {
  canAddParityFilter,
  limitReached,
} from 'lumen/utils/parity-analytics/can-add-parity-filter';

import styles from './breakdown-block.module.css';

// Number of results needed to start slicing the results
const MIN_SLICE_LENGTH = 50;
// Length of items rendered at a time
const SLICE_LENGTH = 25;
// Max amounts of rows in a breakdown ("Other" row excluded)
const NB_OF_BREAKDOWN_ITEMS_TO_DISPLAY = 5;

interface ParityInsightBreakdownBlockSignature {
  Args: {
    hideHotelsMetric: boolean;
    isLoading?: boolean;
    parsedSegmentationFilters: Record<string, string>;
    redirectToTrends: (segmentationName: Breakdown) => void;
    searchValues?: {
      isSearchActive: boolean;
      searchValue: string;
    };
    segmentationBlockIdx: number;
    segmentationData?: {
      segmentationName: string;
      segmentationObjects: unknown[];
    };
    segmentationFilterClicked: (
      segmentationId: string,
      segmentationValueId: string,
    ) => void;
    setIsSearchActive: (segmentationName: Breakdown, isActive: boolean) => void;
    setSearchValue: (segmentationName: Breakdown, searchValue: string) => void;
    showLoadingPlaceholderState: boolean;
    sortKey: ParityFilter;
    sortType: string;
  };
  Element: HTMLDivElement;
}

type NestedBreakdownItemRow = {
  depth: number;
  item: ParityInsightSegmentationItem | BreakdownItemOther;
};

export default class ParityInsightBreakdownBlock extends Component<ParityInsightBreakdownBlockSignature> {
  @service declare intl: Services['intl'];

  @tracked displayedSegmentationObjectsIndex = SLICE_LENGTH;
  @tracked isOtherRowCurrentlyExpanded = false;

  elScrollContainer = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allSegmentationObjects() {
    const rows: NestedBreakdownItemRow[] = [];
    const createOtherRow =
      this.sortedSegmentationObjects?.length > 5 && !this.isSearchActive;

    // Loop over sortedSegmentationObjects as rows
    this.sortedSegmentationObjects.some((row, index) => {
      // If user entered something in the block's search field...
      if (this.isSearchActive && this.searchValue) {
        if (this.fuzzyMatchedItems.has(row)) {
          rows.push({ item: row, depth: 0 }); // Depth is 0 in search mode
        }
        // if there are children, find the matching ones
        if (row.children) {
          const matchingChildren: ParityInsightSegmentationItem[] = [];
          row.children.forEach((segmentationChild) => {
            if (this.fuzzyMatchedItems.has(segmentationChild)) {
              matchingChildren.push(segmentationChild);
            }
          });
          if (matchingChildren.length) {
            // if matches are found, set expanded
            row.isExpanded = true;
            matchingChildren.forEach((child) =>
              rows.push({ item: child, depth: 0 }),
            );
          } else {
            row.isExpanded = false;
          }
        }
      } else {
        // If currently at the 0-indexed position of the 'other' row, and there are more rows to come...
        if (index === NB_OF_BREAKDOWN_ITEMS_TO_DISPLAY - 1 && createOtherRow) {
          // Set the children property of the 'other' row to contain the remaining items of the list up until now
          const otherChildSegmentationObjects =
            this.sortedSegmentationObjects.slice(index);

          const otherRow = new BreakdownItemOther(getOwner(this), {
            isExpanded: this.isOtherRowCurrentlyExpanded,
            children: otherChildSegmentationObjects,
          });

          // Add the other row as a visible row in the list
          rows.push({ item: otherRow, depth: 0 });

          if (otherRow.isExpanded) {
            // If expanded, should show all the 'other' row's children directly underneath
            otherRow.children.forEach((child) =>
              rows.push({ item: child, depth: 1 }),
            );
          }

          // Returning true -> exit loop
          return true;
        }

        // If no 'other' row active, we didn't reach the limit yet. This is the default case, just add.
        rows.push({ item: row, depth: 0 });

        // Support 1-level deep parent/children nesting
        if (row.isExpanded) {
          (row.children ?? []).forEach((child) =>
            rows.push({ item: child, depth: 1 }),
          );
        }
      }

      // Must always return a value in a .some()
      return false;
    });

    return rows;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get arrSegmentationObjects() {
    return this.args.segmentationData?.segmentationObjects;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get blockDisplayName() {
    return this.intl.t(`parity_analytics.${this.segmentationName}`, {
      count: 1,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disabledTooltipMessage() {
    if (
      // @ts-expect-error: Incorrect type
      limitReached(this.args.parsedSegmentationFilters, this.segmentationName)
    ) {
      return this.intl.t(
        'parity_insight.filtering_unavailable.explanation.only_1',
      );
    }

    return this.intl.t(
      'parity_insight.filtering_unavailable.explanation.maximum_reached',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get displayedSegmentationObjects() {
    // Optimize search scenario
    if (this.allSegmentationObjects.length > MIN_SLICE_LENGTH) {
      return this.allSegmentationObjects.slice(
        0,
        this.displayedSegmentationObjectsIndex,
      );
    }
    return this.allSegmentationObjects;
  }

  @cached
  get fuzzyMatchedItems(): Set<ParityInsightSegmentationItem> {
    if (!this.searchValue) {
      return new Set();
    }

    const allItems: ParityInsightSegmentationItem[] = [];
    this.sortedSegmentationObjects.forEach((row) => {
      allItems.push(row, ...(row.children ?? []));
    });

    const fuse = new Fuse(allItems, {
      ignoreLocation: true,
      threshold: 0.3,
      keys: ['formattedSegmentationValue', 'meta.brand_code'],
    });
    return new Set(fuse.search(this.searchValue).map((result) => result.item));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    return !canAddParityFilter(
      // @ts-expect-error: Incorrect type
      this.args.parsedSegmentationFilters,
      this.segmentationName,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSearchActive() {
    return this.args.searchValues?.isSearchActive ?? false;
  }

  // Find the max number of shops in one of the segmentation values
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxNbOfShops() {
    const segmentations = this.allSegmentationObjects;
    let max = 0;

    segmentations.forEach(({ item: segmentation }) => {
      let currentValue = segmentation.calculatedValues.totalNbOfShops;

      if (isOtherRow(segmentation)) {
        let maxFromChildren = 0;

        // Find max in all children instead of other row value
        segmentation.children?.forEach((child) => {
          if (child.calculatedValues.totalNbOfShops > maxFromChildren) {
            maxFromChildren = child.calculatedValues.totalNbOfShops;
          }
        });

        currentValue = maxFromChildren;
      }

      // Is the currently found maximum value bigger than maxBlock?
      if (currentValue > max) {
        max = currentValue;
      }
    });

    return max;
  }

  get searchValue(): string {
    return this.args.searchValues?.searchValue ?? '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationName() {
    return this.args.segmentationData?.segmentationName;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedSegmentationObjects() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const selectedSegmentationValues =
      this.args.parsedSegmentationFilters[
        // @ts-expect-error: Incorrect type
        this.args.segmentationData?.segmentationName
      ];
    const filteredBreakdownKeys = Object.keys(
      this.args.parsedSegmentationFilters,
    );

    if (this.arrSegmentationObjects && this.arrSegmentationObjects.length) {
      // Don't show null values for non-filtered breakdowns
      const filteredSegmentationObjects = (
        this.arrSegmentationObjects as ParityInsightSegmentationItem[]
      ).filter((segObj) => {
        const isBreakdownFiltered = filteredBreakdownKeys.includes(
          segObj.segmentationId,
        );
        if (!isBreakdownFiltered && segObj.hasZeroValues) {
          return false;
        }

        return true;
      });

      return filteredSegmentationObjects.sort((a, b) => {
        // Sorting by selected state yes/no. And within those two groups by loss, meet, and win.
        // Determining which of the two is selected.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const isSelectedA =
          selectedSegmentationValues &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          selectedSegmentationValues.indexOf(a.segmentationValueId) !== -1;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const isSelectedB =
          selectedSegmentationValues &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          selectedSegmentationValues.indexOf(b.segmentationValueId) !== -1;

        // Transforming the selected state into a number used by the sort function
        const sortA = isSelectedA ? 0 : 1;
        const sortB = isSelectedB ? 0 : 1;

        // If either one selected when the other is not, we sort according to selection state
        if (sortA !== sortB) {
          return sortA - sortB;
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const sortType = PA_SORT_TYPES[this.args.sortType];

        // If both are selected or unselected, then we sort according to the loss, meet, win of the values
        if (
          this.compareWinMeetLoss(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            a[sortType],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            b[sortType],
            this.args.sortKey,
          ) !== 0
        ) {
          return this.compareWinMeetLoss(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            a[sortType],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            b[sortType],
            this.args.sortKey,
          );
        }
        if (
          a.unfilteredData &&
          b.unfilteredData &&
          this.compareWinMeetLoss(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            a.unfilteredData[sortType],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            b.unfilteredData[sortType],
            this.args.sortKey,
          ) !== 0
        ) {
          // If comparing normal filtered values results equal, compare win/meet/loss of the unfiltered data
          return this.compareWinMeetLoss(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            a.unfilteredData[sortType],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            b.unfilteredData[sortType],
            this.args.sortKey,
          );
        }

        return a.segmentationValueId.localeCompare(b.segmentationValueId);
      });
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get titleParams() {
    return {
      segmentationName: this.segmentationName,
      isLoading: this.args.showLoadingPlaceholderState,
      searchValues: this.args.searchValues,
      blockDisplayName: this.blockDisplayName,
      updateSearchValue: this.updateSearchValue.bind(this),
      updateIsSearchActive: this.updateIsSearchActive.bind(this),
    };
  }

  willDestroy(): void {
    super.willDestroy();

    this.elScrollContainer = null;
  }

  // Will return [..., -1], 0, [1, ...] based on comparing loss, then meet, then win.
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  compareWinMeetLoss(a, b, key) {
    // Default sorting order
    const sortingOrder = ['loss', 'meet', 'win'];

    // Change sorting order by selection i.e.: [meet, loss, win] if key is 'meet'
    // 1. Remove key 2. Readd key as first elementof array
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    sortingOrder.splice(sortingOrder.indexOf(key), 1);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    sortingOrder.splice(0, 0, key);

    // Three-level sorting based on the sortingOrder array
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (a[sortingOrder[0]] !== b[sortingOrder[0]]) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return b[sortingOrder[0]] - a[sortingOrder[0]];
    }
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (a[sortingOrder[1]] !== b[sortingOrder[1]]) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return b[sortingOrder[1]] - a[sortingOrder[1]];
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return b[sortingOrder[2]] - a[sortingOrder[2]];
  }

  // Sorting function which expects an object containing { win, meet, loss } values
  // @ts-expect-error: Incorrect type
  updateIsSearchActive(isSearchActive): void {
    if (!isSearchActive) {
      this.resetIndex();
      // Collapse the segmentations again when stopping the search
      // TODO: this is a side effect on a computed property
      // This is should not be done this way
      this.displayedSegmentationObjects.forEach(
        ({ item: segmentationValue }) => {
          segmentationValue.isExpanded = false;
        },
      );
    }
    if (this.elScrollContainer) {
      // @ts-expect-error: Incorrect type
      this.elScrollContainer.scrollTop = 0;
    }
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.args.setIsSearchActive(this.segmentationName, isSearchActive);
  }

  // helper function to calculate the total values of all passed segmentation objects in the array
  // @ts-expect-error: Incorrect type
  updateSearchValue(searchValue): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.args.setSearchValue(this.segmentationName, searchValue);
  }

  @action
  // @ts-expect-error: Incorrect type
  didInsertInfiniteScroll(element): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.elScrollContainer = element;
  }

  @action
  expandPopup(): void {
    // @ts-expect-error: Incorrect type
    this.args.redirectToTrends(this.segmentationName);
  }

  @action
  loadMore(): void {
    const currentSearchIndexText = this.displayedSegmentationObjectsIndex;
    // Stop loading more if index is greater than the number of results
    if (currentSearchIndexText < this.allSegmentationObjects?.length) {
      this.displayedSegmentationObjectsIndex =
        currentSearchIndexText + SLICE_LENGTH;
    }
  }

  @action
  resetIndex(): void {
    this.displayedSegmentationObjectsIndex = SLICE_LENGTH;
  }

  @action
  // @ts-expect-error: Incorrect type
  setIsOtherRowCurrentlyExpanded(value): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.isOtherRowCurrentlyExpanded = value;
  }

  @action
  // @ts-expect-error: Incorrect type
  toggleSearch(isEnabled): void {
    this.updateIsSearchActive(isEnabled);
  }

  @action
  triggerScrollbarUpdate(hasToScrollDown = false): void {
    if (hasToScrollDown) {
      // We scroll down a bit so the user can see that the segmentation value is expanded and that he can scroll down in the blocks
      setTimeout(() => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.elScrollContainer.scrollBy({
          top: 120,
          behavior: 'smooth',
        });
      });
    }
  }

  <template>
    {{#if @isLoading}}
      <PrismCard @contentLayout="noPadding">
        <:header>
          <LoadingBar />
        </:header>
        <:content>
          <div class={{styles.container}} />
        </:content>
      </PrismCard>
    {{else}}
      <div ...attributes>
        <PrismCard
          @contentLayout="noPadding"
          class="parity relative overflow-hidden"
        >
          <:header as |Header|>
            <ParityInsightBreakdownBlockTitle
              {{! @glint-expect-error: Incorrect type }}
              @cardHeader={{Header}}
              @disabledTooltipMessage={{this.disabledTooltipMessage}}
              @isDisabled={{this.isDisabled}}
              {{! @glint-expect-error: Incorrect type }}
              @params={{this.titleParams}}
            />
          </:header>

          <:headerAfter>
            <div class="flex items-center gap-50">
              {{#if this.isSearchActive}}
                <PrismButton
                  @buttonType="ghost"
                  @icon="cross"
                  @onClick={{fn this.toggleSearch false}}
                />
              {{else}}
                <PrismButton
                  @buttonType="ghost"
                  @icon="search"
                  @onClick={{fn this.toggleSearch true}}
                  data-event-id="market-parity-insight-breakdown-search"
                  data-event-value={{this.segmentationName}}
                />
                <PrismButton
                  @buttonType="ghost"
                  @icon="expand"
                  @onClick={{this.expandPopup}}
                  data-event-id="market-parity-insight-breakdown-expand"
                  data-event-value={{this.segmentationName}}
                />
              {{/if}}
            </div>
          </:headerAfter>

          <:content>
            <div class={{styles.container}}>
              <InfiniteScroller
                @onLoadMore={{this.loadMore}}
                @percent={{85}}
                class={{styles.infinite-scroller}}
                {{didInsert this.didInsertInfiniteScroll}}
              >
                {{#if this.displayedSegmentationObjects.length}}
                  {{#each
                    this.displayedSegmentationObjects
                    as |segmentationValue|
                  }}
                    <ParityInsightBreakdownValue
                      {{! @glint-expect-error: Incorrect type }}
                      @blockKey={{@segmentationData.segmentationName}}
                      {{! @glint-expect-error: Incorrect type }}
                      @data={{segmentationValue.item}}
                      @depth={{segmentationValue.depth}}
                      @hideHotelsMetric={{@hideHotelsMetric}}
                      @isDisabled={{this.isDisabled}}
                      @isSearchActive={{this.isSearchActive}}
                      {{! @glint-expect-error: Incorrect type }}
                      @maxBlockValue={{this.maxBlockValue}}
                      @maxNbOfShops={{this.maxNbOfShops}}
                      @onSegmentationFilterClicked={{@segmentationFilterClicked}}
                      {{! @glint-expect-error: Incorrect type }}
                      @parsedSegmentationFilters={{@parsedSegmentationFilters}}
                      @resetIndex={{this.resetIndex}}
                      @segmentationBlockIdx={{@segmentationBlockIdx}}
                      @setIsOtherRowCurrentlyExpanded={{this.setIsOtherRowCurrentlyExpanded}}
                      @sortType={{@sortType}}
                      {{! @glint-expect-error: Incorrect type }}
                      @triggerScrollbarUpdate={{this.triggerScrollbarUpdate}}
                    />
                  {{/each}}
                {{else if @showLoadingPlaceholderState}}
                  <ParityInsightBreakdownValueLoading />
                  <ParityInsightBreakdownValueLoading />
                  <ParityInsightBreakdownValueLoading />
                  <ParityInsightBreakdownValueLoading />
                  <ParityInsightBreakdownValueLoading />
                {{else}}
                  <p class="text-300--regular mt-200 ml-500">
                    {{t "general.no_results"}}
                  </p>
                {{/if}}
              </InfiniteScroller>
            </div>
          </:content>
        </PrismCard>
      </div>
    {{/if}}
  </template>
}
